import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowRightLeft,
  ArrowUpRight,
  BadgeCheck,
  Banknote,
  Bell,
  BookOpen,
  Bookmark,
  Bot,
  Boxes,
  Briefcase,
  BriefcaseBusiness,
  Building,
  Building2,
  Calendar,
  CalendarCheck,
  CalendarClock,
  CalendarCog,
  CalendarDays,
  CalendarRange,
  Camera,
  ChartColumn,
  ChartLine,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Code,
  Contact,
  CreditCard,
  Download,
  Factory,
  FileCheck2,
  FileLock,
  FileMinus,
  FileQuestion,
  FileSignature,
  FileSpreadsheet,
  FileText,
  Folder,
  FolderKanban,
  Gauge,
  Gift,
  GraduationCap,
  HandCoins,
  Handshake,
  Hash,
  Heart,
  History,
  House,
  IdCard,
  Inbox,
  Kanban,
  KeyRound,
  Landmark,
  LayoutDashboard,
  LayoutGrid,
  LayoutTemplate,
  LifeBuoy,
  List,
  ListTodo,
  ListTree,
  Lock,
  Mail,
  Megaphone,
  MessageCircle,
  MessagesSquare,
  MonitorSmartphone,
  Network,
  Package,
  PackagePlus,
  Palette,
  Percent,
  PiggyBank,
  Plug,
  Puzzle,
  Receipt,
  ReceiptText,
  Repeat,
  Route,
  Ruler,
  Scale,
  Settings2,
  Share2,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  Star,
  Store,
  Tags,
  Target,
  TextCursorInput,
  ThumbsDown,
  ToggleLeft,
  Trash2,
  TrendingUp,
  TriangleAlert,
  Truck,
  UserCheck,
  UserCog,
  UserRound,
  Users,
  Wallet,
  Warehouse,
  Webhook,
  Workflow,
  Zap,
} from 'lucide-react';
import type { ConfigIcones } from '@malgoos/shared';
import { useSessaoOpcional } from './sessao';

/**
 * D2 (ícones nas abas, plano `demandas/mapa/LOTE-2026-10-09-PLANO.md`, "Ícones nos sub-menus e nas sub-páginas"): ícone nas
 * abas que já existem (sem sub-itens no menu lateral), no primeiro nível (módulo ou grupo) e no segundo (a aba), e nas abas
 * internas (Minha conta, Notificações, Usuários e papéis). O cliente liga ou desliga, escolhe se aparece no celular e troca o
 * ícone de cada aba em Admin > Configurações > Sub-sistemas (configuração `icones` do workspace). Ligado é o padrão (D2:
 * ícone sempre visível, `iconesEfetivos`); só o desligar explícito tira, e as abas ficam como antes. O glifo segue o do menu lateral (`size-4 shrink-0`, `aria-hidden`, cor do texto): nenhum token novo.
 */

/** Ícones que o cliente pode escolher (os da tabela do plano e alguns a mais), pelo nome do componente. */
export const ICONES: Readonly<Record<string, LucideIcon>> = {
  Activity, ArrowDownLeft, ArrowLeftRight, ArrowRightLeft, ArrowUpRight, BadgeCheck, Banknote, Bell, BookOpen, Bookmark, Bot, Boxes, Briefcase, BriefcaseBusiness,
  Building, Building2, Calendar, CalendarCheck, CalendarClock, CalendarCog, CalendarDays, CalendarRange, Camera, ChartColumn, ChartLine, ClipboardCheck, ClipboardList,
  Clock, Code, Contact, CreditCard, Download, Factory, FileCheck2, FileLock, FileMinus, FileQuestion, FileSignature, FileSpreadsheet, FileText, Folder, FolderKanban,
  Gauge, Gift, GraduationCap, HandCoins, Handshake, Hash, Heart, History, House, IdCard, Inbox, Kanban, KeyRound, Landmark, LayoutDashboard, LayoutGrid,
  LayoutTemplate, LifeBuoy, List, ListTodo, ListTree, Lock, Mail, Megaphone, MessageCircle, MessagesSquare, MonitorSmartphone, Network, Package, PackagePlus, Palette, Percent,
  PiggyBank, Plug, Puzzle, Receipt, ReceiptText, Repeat, Route, Ruler, Scale, Settings2, Share2, ShieldCheck, ShoppingCart, SlidersHorizontal, Star, Store, Tags,
  Target, TextCursorInput, ThumbsDown, ToggleLeft, Trash2, TrendingUp, TriangleAlert, Truck, UserCheck, UserCog, UserRound, Users, Wallet, Warehouse, Webhook,
  Workflow, Zap,
};

/**
 * Tabela do plano. Chave: `<slug>.<aba>` (segundo nível), `modulo:<slug>` (primeiro nível por módulo), `grupo:<menu>.<grupo>`
 * (primeiro nível do Cadastros) e as abas internas (`conta.<aba>`, `notificacoes.<aba>`, `usuarios.<sub-aba>`).
 */
export const ICONES_PADRAO: Readonly<Record<string, string>> = {
  'grupo:cadastros.cadastros': 'Contact',
  'grupo:cadastros.servicos': 'BriefcaseBusiness',
  'grupo:cadastros.financeiro': 'Wallet',
  'grupo:cadastros.agenda': 'CalendarDays',
  'grupo:cadastros.estoque': 'Package',
  'modulo:pipeline': 'Kanban',
  'modulo:conversas': 'MessagesSquare',
  'modulo:assinaturas': 'Repeat',
  'modulo:portal': 'MonitorSmartphone',
  'modulo:pessoas': 'Users',
  'modulo:folha': 'HandCoins',
  'modulo:ausencias': 'CalendarRange',
  'modulo:despesas': 'Receipt',
  'modulo:documentos': 'Folder',
  'modulo:ponto': 'Clock',
  'modulo:estoque': 'Boxes',
  'modulo:compras': 'ShoppingCart',
  'modulo:tarefas': 'ListTodo',
  'modulo:admin': 'ShieldCheck',
  'modulo:configuracoes': 'Settings2',
  'cadastros.pessoas': 'Building2',
  'cadastros.projetos': 'FolderKanban',
  'cadastros.classificacao': 'Tags',
  'cadastros.servicos': 'BookOpen',
  'cadastros.servicos-contratados': 'FileCheck2',
  'cadastros.condicoes-pagamento': 'CalendarRange',
  'cadastros.formas-pagamento': 'CreditCard',
  'financeiro.centros-custo': 'Network',
  'financeiro.contas': 'Landmark',
  'financeiro.categorias': 'ListTree',
  'agenda.recursos': 'UserCheck',
  'estoque.produtos': 'Package',
  'pipeline.pipeline': 'Kanban',
  'pipeline.equipes': 'Users',
  'pipeline.metas': 'Target',
  'pipeline.motivos-perda': 'ThumbsDown',
  'pipeline.atividades': 'CalendarCheck',
  'conversas.whatsapp': 'MessageCircle',
  'conversas.instagram': 'Camera',
  'conversas.email': 'Mail',
  'conversas.portal': 'MonitorSmartphone',
  'conversas.respostas-rapidas': 'Zap',
  'conversas.templates': 'LayoutTemplate',
  'conversas.filas': 'Inbox',
  'conversas.automacoes': 'Workflow',
  // Item 11 do lote de 19/10: caixa do chat do site e cadastro dos widgets (o código para colar).
  'conversas.site': 'Store',
  'conversas.chat-site': 'Code',
  'assinaturas.contratos': 'FileSignature',
  // Pacote 2 (onda 1a): catálogo de planos.
  'assinaturas.planos': 'Package',
  'assinaturas.reajustes': 'TrendingUp',
  'assinaturas.onboarding': 'ClipboardCheck',
  'portal.acessos': 'KeyRound',
  'portal.configuracao': 'SlidersHorizontal',
  'contas.a-receber': 'ArrowDownLeft',
  'contas.a-pagar': 'ArrowUpRight',
  'contas.cobrancas': 'ReceiptText',
  'contas.recorrencias': 'Repeat',
  'contas.notas-credito': 'FileMinus',
  'financeiro.entradas-saidas': 'ArrowLeftRight',
  'financeiro.conciliacao': 'Scale',
  'financeiro.fluxo-caixa': 'ChartColumn',
  'financeiro.indices': 'Percent',
  'relatorios.inicio': 'LayoutGrid',
  'relatorios.painel-grupo': 'Building2',
  'relatorios.inadimplencia': 'TriangleAlert',
  'relatorios.dre': 'TrendingUp',
  'relatorios.fluxo': 'ChartColumn',
  'relatorios.extrato': 'ArrowLeftRight',
  'relatorios.centros': 'Landmark',
  'relatorios.parceiros': 'Users',
  'relatorios.mrr': 'Repeat',
  'relatorios.margem': 'Percent',
  'relatorios.quadro': 'Users',
  'relatorios.folha': 'Banknote',
  'relatorios.exportacoes': 'Download',
  'agenda.semana': 'CalendarRange',
  'agenda.dia': 'Calendar',
  'agenda.mes': 'CalendarDays',
  'pessoas.funcionarios': 'IdCard',
  'pessoas.departamentos': 'Building',
  'pessoas.funcoes': 'Briefcase',
  'pessoas.contratos': 'FileSignature',
  'pessoas.documentos': 'FileLock',
  'pessoas.jornada': 'Clock',
  'pessoas.habilidades': 'BadgeCheck',
  'folha.beneficios': 'Gift',
  'folha.eventos': 'ListTodo',
  'folha.competencias': 'CalendarCog',
  'folha.holerites': 'FileSpreadsheet',
  'folha.configuracao': 'SlidersHorizontal',
  'estoque.variacoes': 'Ruler',
  'estoque.depositos': 'Warehouse',
  'estoque.movimentacoes': 'ArrowRightLeft',
  'estoque.reposicao': 'PackagePlus',
  'estoque.inventario': 'ClipboardCheck',
  'estoque.lotes': 'CalendarClock',
  'compras.compras': 'ShoppingCart',
  'compras.requisicoes': 'ClipboardList',
  'compras.cotacoes': 'FileQuestion',
  'compras.pedidos': 'FileText',
  'compras.recebimentos': 'Truck',
  'compras.fornecedores': 'Store',
  'tarefas.kanban': 'Kanban',
  'tarefas.lista': 'List',
  'tarefas.minhas': 'UserRound',
  'tarefas.painel': 'LayoutDashboard',
  'tarefas.quadros': 'LayoutGrid',
  // Pacote 2 (onda 1a): Projetos e tarefas.
  'tarefas.projetos': 'FolderKanban',
  'tarefas.marcos': 'CalendarCheck',
  'equipe.chat': 'MessagesSquare',
  'treinamento.cursos': 'GraduationCap',
  'treinamento.trilhas': 'Route',
  'treinamento.progresso': 'ChartLine',
  // Pacote 2 (onda 1a).
  'ausencias.pedidos': 'ClipboardList',
  'ausencias.aprovacoes': 'ClipboardCheck',
  'ausencias.calendario': 'CalendarDays',
  'ausencias.saldos': 'Scale',
  'ausencias.tipos': 'Tags',
  'despesas.despesas': 'Receipt',
  'despesas.aprovacoes': 'ClipboardCheck',
  'despesas.reembolsos': 'HandCoins',
  'despesas.centros': 'Network',
  'documentos.arquivos': 'Folder',
  'documentos.contratos': 'FileSignature',
  'documentos.assinaturas': 'FileCheck2',
  // Registro de ponto (lote 1, 19/10).
  'ponto.marcar': 'Clock',
  'ponto.marcacoes': 'List',
  'ponto.espelho': 'CalendarDays',
  'ponto.ajustes': 'ClipboardCheck',
  'ponto.banco': 'PiggyBank',
  'ponto.fechamentos': 'Lock',
  'ponto.fiscal': 'FileLock',
  'ponto.configuracao': 'CalendarCog',
  'automacoes.painel': 'Gauge',
  'automacoes.feriados': 'CalendarDays',
  'automacoes.fluxos': 'Workflow',
  'automacoes.broadcast': 'Megaphone',
  'integracoes.conectores': 'Plug',
  'integracoes.webhooks': 'Webhook',
  'integracoes.api': 'Code',
  'integracoes.mcp': 'Bot',
  'integracoes.conta-azul': 'ArrowRightLeft',
  'admin.painel': 'Gauge',
  'admin.modulos': 'Puzzle',
  'admin.tenants': 'Building2',
  'admin.usuarios': 'UserCog',
  'admin.auditoria': 'ShieldCheck',
  'admin.saude': 'Activity',
  'admin.erros': 'TriangleAlert',
  'admin.historico-telas': 'History',
  'admin.tela-entrada': 'MonitorSmartphone',
  'admin.uso-ia': 'Gauge',
  'configuracoes.empresa': 'Building',
  'configuracoes.aparencia': 'Palette',
  'configuracoes.modos': 'ToggleLeft',
  'configuracoes.subsistemas': 'LayoutGrid',
  'configuracoes.sequencias': 'Hash',
  'configuracoes.campos': 'TextCursorInput',
  'configuracoes.campos-telas': 'ListTodo',
  'configuracoes.visoes': 'Bookmark',
  'configuracoes.modelos': 'LayoutTemplate',
  'configuracoes.estruturas': 'Workflow',
  'configuracoes.crm': 'Handshake',
  'conta.perfil': 'UserRound',
  'conta.meus-dados': 'UserCheck',
  'conta.aparencia': 'Palette',
  'conta.notificacoes': 'Bell',
  'conta.seguranca': 'Lock',
  'conta.assistentes': 'Bot',
  'conta.lixeira': 'Trash2',
  'usuarios.membros': 'Users',
  'usuarios.convites': 'Mail',
  'usuarios.papeis': 'ShieldCheck',
  'usuarios.pedidos': 'ClipboardCheck',
  'notificacoes.caixa': 'Inbox',
  'notificacoes.preferencias': 'SlidersHorizontal',
  'notificacoes.workspace': 'Building2',
};

/** Nome do ícone da chave: o escolhido pelo cliente (se existir no catálogo) ou o da tabela. */
export function nomeDoIcone(chave: string, trocas?: Record<string, string>): string | undefined {
  const escolhido = trocas?.[chave];
  return escolhido && ICONES[escolhido] ? escolhido : ICONES_PADRAO[chave];
}

export interface IconesDaSessao {
  ativo: boolean;
  /** Mostrar no celular (abaixo de `sm`). */
  celular: boolean;
  /** O glifo da chave (ou nada, desligado ou sem ícone): para o `icone` do item da aba. */
  de(chave: string): ReactNode | undefined;
}

/** Glifo do ícone, no padrão do menu lateral; `celular` falso esconde abaixo de `sm`. */
export function glifoDoIcone(chave: string, c: ConfigIcones | undefined): ReactNode | undefined {
  if (!c?.ativo) return undefined;
  const nome = nomeDoIcone(chave, c.trocas);
  const Icone = nome ? ICONES[nome] : undefined;
  if (!Icone) return undefined;
  return <Icone className={`size-4 shrink-0 ${c.celular === false ? 'hidden sm:block' : ''}`} aria-hidden data-icone={nome} />;
}

/** D2: a configuração valendo, com os ícones ligados quando o workspace não desligou explicitamente. */
export function iconesEfetivos(salvo: ConfigIcones | undefined): ConfigIcones {
  return { ...salvo, ativo: salvo?.ativo !== false };
}

/** Configuração de ícones do workspace (da sessão), ligada por padrão (D2, decisão do Gustavo: ícone sempre visível);
 * só o "desligar" explícito tira. Fora da sessão (testes de componente), desligada. */
export function useIcones(): IconesDaSessao {
  const sessao = useSessaoOpcional();
  const salvo = sessao?.subsistemas?.workspace.icones;
  const c = sessao ? iconesEfetivos(salvo) : undefined;
  return { ativo: !!c?.ativo, celular: c?.celular !== false, de: (chave) => glifoDoIcone(chave, c) };
}
